import { PhnxAvatar } from '@/components/01-atoms/Avatar/Avatar'
import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxTag } from '@/components/01-atoms/Tag/Tag'

import styles from './Card.module.css'

export type CardProps = {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary'
    | 'quinary'
    | 'senary'
    | 'septenary'
    | 'podcast'
  layout?: 'vertical' | 'horizontal'
  alignment?: 'default' | 'center'
  style?: 'square' | 'top' | 'bottom' | 'float' // if they choose quaternary variant this isn't needed as it has no drop shadow and an outlined image
  feature?: boolean
  asset?: React.ReactNode
  title: string
  subtitle?: React.ReactNode
  description?: string
  body?: any
  button?: any
  tags?: string[]
  author?: React.ReactNode
  link?: string // from blok pass the button href here too please
  insightType?: string
  duration?: string
  titleOverwrite?: boolean
}

export const PhnxCard: React.FC<CardProps> = ({
  variant = 'primary',
  layout = 'horizontal',
  alignment = 'center',
  style = 'square',
  feature = false,
  asset,
  title,
  subtitle,
  description,
  body,
  button,
  tags,
  author,
  link,
  insightType,
  duration,
  titleOverwrite = false,
}) => {
  let lowerInsightType = insightType?.toLowerCase()

  const removePodcastFromTitle = (title: string) => {
    // if no title return an empty string
    if (!title) return ''
    return title.replace(' | Podcast', '')
  }

  const podcastInnerContent = (
    <div className={styles['podcast-inner-content']}>
      <div className={styles['podcast-play-duration-container']}>
        <div className={styles['podcast-play-btn']}>
          <img src="/assets/play-button.png" alt="Play" />
        </div>
        {duration && <div className={styles['podcast-duration']}>{duration}</div>}
      </div>
      <div
        className={styles['podcast-asset-container']}
        style={{
          // @ts-ignore
          backgroundImage: `url(${asset?.props?.images[0].src})`,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          }}
        />
        <div
          className={styles['phnxcard-title']}
          data-testid="title"
          style={{ position: 'relative', zIndex: 1 }}
        >
          <h3>{removePodcastFromTitle(title)}</h3>
        </div>
      </div>
    </div>
  )

  const innerContent = (
    <>
      {asset && variant !== 'podcast' && (
        <div className={styles['phnxcard-asset-holder']} data-testid="asset-holder">
          {asset}
          {lowerInsightType && (lowerInsightType === 'video' || lowerInsightType === 'webinar') && (
            <div className={styles['play-btn']} />
          )}
        </div>
      )}
      <div className={`${styles['phnxcard-text-holder']} ${styles[`alignment-${alignment}`]}`}>
        {variant === 'podcast' && (
          <div>
            <div className={styles['podcast-play-btn']}>test</div>
          </div>
        )}
        {variant === 'podcast' && <div className={styles['podcast-duration']}>Duration</div>}
        {title && (
          <div
            className={`${styles['phnxcard-title']} ${titleOverwrite ? styles['title-overwrite'] : ''}`}
            data-testid="title"
          >
            <h3>{variant === 'podcast' ? `podcast ${title}` : title}</h3>
          </div>
        )}
        {subtitle && variant !== 'tertiary' && variant !== 'podcast' && (
          <div className={styles['phnxcard-subtitle']} data-testid="subtitle">
            {subtitle}
          </div>
        )}
        {description && (
          <div className={styles['phnxcard-description']} data-testid="description">
            {description}
            {body}
          </div>
        )}
        {!description && body && (
          <div className={styles['phnxcard-description']} data-testid="description">
            {body}
          </div>
        )}
        {author && variant !== 'podcast' && (
          <div className={styles['phnxcard-author']} data-testid="author">
            {author}
          </div>
        )}
        {!author && variant === 'secondary' && (
          <div className={styles['phnxcard-noauthor']} data-testid="noauthor">
            <PhnxAvatar
              asset={{
                src: '/assets/findex_contributer.png',
                alt: 'Findex Contributor',
              }}
              size="small"
              style="circle"
            />
            <div className={styles['phnxcard-author']} data-testid="author">
              By Findex Contributor
            </div>
          </div>
        )}
        {button && lowerInsightType !== 'video' && lowerInsightType !== 'webinar' && (
          <div className={styles['phnxcard-button-holder']} data-testid="button">
            {button}
          </div>
        )}
        {lowerInsightType && (lowerInsightType === 'video' || lowerInsightType === 'webinar') && (
          <div className={styles['phnxcard-video-button']} data-testid="video-button">
            <PhnxButton
              variant="quinary"
              size="small"
              type="link"
              label="Watch Now"
              href={button?.props?.href ?? ''}
            />
          </div>
        )}
        {tags && variant === 'secondary' && (
          <div className={styles['phnxcard-tag-holder']}>
            {tags.map((item, index) => (
              <PhnxTag key={index} title={item} isLight isBold />
            ))}
          </div>
        )}
      </div>
    </>
  )

  const classNames = `
    ${styles['phnxcard']}
    ${styles[`variant-${variant}`] || ''}
    ${styles[`style-${style}`] || ''}
    ${styles[`layout-${layout}`] || ''}
    ${styles[feature ? 'feature' : 'notfeature'] || ''}
    ${styles[link ? 'linkhand' : '']}
  `.trim()

  const handleClick = () => {
    // if link contains /en-nz/ then we need to remove it
    let linkUpdate = link?.replace('/en-nz/', '/')
    linkUpdate && (window.location.href = linkUpdate)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick()
    }
  }

  return (
    <>
      {link ? (
        <div
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          className={classNames}
          data-testid="phnxcard"
          data-test-variant={variant}
          role="button"
          aria-pressed="false"
        >
          {variant === 'podcast' ? podcastInnerContent : innerContent}
        </div>
      ) : (
        <div className={classNames} data-testid="phnxcard" data-test-variant={variant}>
          {variant === 'podcast' ? podcastInnerContent : innerContent}
        </div>
      )}
    </>
  )
}
